/**
 * Alta de un usuario del tenant (#70).
 *
 * No pide contraseña, y es deliberado: **la genera el servidor** y la devuelve
 * una única vez. Un administrador que elige la contraseña de otra persona
 * elige "Marca2026" trece veces, y encima queda sabiendo la credencial de un
 * tercero — con lo que el registro de auditoría ya no puede distinguir quién
 * actuó. Ver `services/api/schemas/users.py`.
 *
 * El rol por defecto es el menor (`viewer`): un alta apurada tiene que dar el
 * acceso más chico, no el más grande.
 */
import { useState, type ReactNode } from 'react';

import type { ApiError } from '@/api/errors';
import type { Role } from '@/api/types';
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from '@/auth/roles';
import { useI18n } from '@/i18n/useI18n';
import { describeApiError } from '@/i18n/errorMessages';
import { Button } from '@/ui/Button';
import { Dialog } from '../products/ui/Dialog';
import { SelectField, TextField } from '../products/ui/fields';
import { ASSIGNABLE_ROLES, isRole, type UserDraft } from './users';

export interface UserCreateDialogProps {
  open: boolean;
  busy: boolean;
  error: ApiError | null;
  onCancel: () => void;
  onSubmit: (draft: UserDraft) => void;
}

export function UserCreateDialog({
  open,
  busy,
  error,
  onCancel,
  onSubmit,
}: UserCreateDialogProps): ReactNode {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [touched, setTouched] = useState(false);

  if (!open) return null;

  // Validación mínima del lado del cliente: la de verdad la hace la API, que
  // además es la única que sabe si el correo ya está tomado en esta marca.
  const emailError =
    touched && !/^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/.test(email.trim())
      ? t('users.form.emailInvalid')
      : undefined;

  const submit = (): void => {
    setTouched(true);
    if (!/^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/.test(email.trim())) return;
    onSubmit({
      email: email.trim(),
      full_name: fullName.trim() === '' ? null : fullName.trim(),
      role,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={t('users.form.title')}
      description={t('users.form.description')}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('confirm.cancel')}
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy}>
            {busy ? t('users.form.submitting') : t('users.form.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label={t('users.form.email')}
          value={email}
          onChange={setEmail}
          hint={t('users.form.emailHint')}
          error={emailError}
          required
          placeholder="maria@labfarma.demo"
        />
        <TextField
          label={t('users.form.name')}
          value={fullName}
          onChange={setFullName}
          hint={t('users.form.nameHint')}
        />
        <SelectField
          label={t('users.form.role')}
          value={role}
          onChange={(value) => {
            if (isRole(value)) setRole(value);
          }}
          hint={ROLE_DESCRIPTIONS[role]}
          options={ASSIGNABLE_ROLES.map((value) => ({
            value,
            label: ROLE_LABELS[value],
          }))}
        />
        {error !== null && (
          <p role="alert" className="text-sm font-medium text-critical">
            {describeApiError(error, t).title}
          </p>
        )}
      </div>
    </Dialog>
  );
}
